/**
 * HeroPanel — unified HERO window (class selection + equipment + stats + bag).
 *
 * Keeps ALL original functionality: equip/unequip, stats, consumables,
 * inventory grid, and the campfire-style class chooser. The chooser now picks
 * 1 OR 2 heroes (the active party), and equipment is per-hero: when two heroes
 * are active a small toggle selects which one the equip view is acting on.
 *
 * @module ui/panels/HeroPanel
 */

import { BasePanel } from './BasePanel.js';
import { equip, unequip, lockRemainingMs, formatLockTime } from '../../core/inventory.js';
import { heroDerivedStats } from '../../core/combat.js';
import { renderEquipmentSlots, renderBagGrid } from '../helpers/slotRenderer.js';
import { tooltip } from '../helpers/tooltipManager.js';

const ROLE_LABEL = { tank: 'Tanque', dps: 'Dano', support: 'Suporte' };
const CLASS_ORDER = ['knight', 'rogue', 'ranger', 'pyromancer', 'priest'];
const MAX_PARTY = 2;

const LEFT_SLOTS = [
  ['helmet', '🪖', 'Elmo'],
  ['chest', '🛡️', 'Peito'],
  ['gloves', '🧤', 'Luvas'],
  ['weapon', '⚔️', 'Arma'],
];
const RIGHT_SLOTS = [
  ['amulet', '📿', 'Amuleto'],
  ['ring1', '💍', 'Anel I'],
  ['ring2', '💍', 'Anel II'],
  ['boots', '🥾', 'Botas'],
];

export class HeroPanel extends BasePanel {
  constructor(opts) {
    super({
      name: 'hero',
      title: 'HERÓI',
      tabs: [
        { id: 'equip', label: 'Equipar' },
        { id: 'select', label: 'Heróis' },
      ],
      state: opts.state,
      data: opts.data,
      hooks: opts.hooks,
    });

    // Selection (1 or 2 class ids) and which active hero the equip view targets.
    this._selectedClasses = this.state.party.map((h) => h.classId);
    this._focusClass = this._selectedClasses[0] ?? CLASS_ORDER[0];
    this._equipHeroIndex = 0;
    this._forcedSelect = false;
    this._openOnSelect = false; // open straight on the hero-select tab next time

    // Build select view DOM (swapped in during render)
    this._buildSelectView();
  }

  // ---- Views ----

  _buildSelectView() {
    this._selectView = document.createElement('div');
    this._selectView.className = 'pd-select-view';
    this._selectView.hidden = true;
    // Campfire scene (reference 6): the five classes stand around a bonfire in
    // a night forest; clicking one steps it forward into the party.
    this._selectView.innerHTML = `
      <div class="pd-select-scene">
        <div class="pd-select-title">Seleção de Herói</div>
        <div class="pd-select-name"></div>
        <div class="pd-select-desc"></div>
        <img class="pd-camp-fire" src="assets/sprites/ui/campfire_fire.png" alt="" />
        <div class="pd-camp-glow"></div>
        <div class="pd-hero-cards"></div>
      </div>
      <div class="pd-select-count"></div>
      <div class="pd-select-actions">
        <button class="pd-btn pd-btn-secondary pd-sel-back" hidden>Voltar</button>
        <button class="pd-btn pd-btn-primary pd-sel-confirm">Confirmar</button>
      </div>`;
    this.body.appendChild(this._selectView);

    // Populate the heroes around the fire (in-game walk sprites, not portraits;
    // right-side heroes are flipped by CSS so everyone faces the flames).
    const cardsEl = this._selectView.querySelector('.pd-hero-cards');
    for (const id of CLASS_ORDER.filter((c) => this.data.classes[c])) {
      const cls = this.data.classes[id];
      const card = document.createElement('button');
      card.className = 'pd-hero-card';
      card.dataset.id = id;
      card.innerHTML = `
        <span class="pd-hero-card-arrow">▼</span>
        <span class="pd-hero-card-name">${cls.label}</span>
        <img src="assets/sprites/anim/${id}_walk_0.png" alt="${cls.label}">
        <span class="pd-hero-card-lv">Nv.${this._classLevel(id)}</span>`;
      card.addEventListener('click', () => this._pickClass(id));
      cardsEl.appendChild(card);
    }

    this._selectView.querySelector('.pd-sel-confirm').addEventListener('click', () => {
      if (!this._selectedClasses.length) return; // need at least one hero
      this.hooks.onChooseParty?.([...this._selectedClasses]);
      this._equipHeroIndex = 0;
      this._forcedSelect = false;
      this.switchTab('equip');
    });
    this._selectView.querySelector('.pd-sel-back').addEventListener('click', () => {
      this.switchTab('equip');
    });

    // Equip view container
    this._equipView = document.createElement('div');
    this._equipView.className = 'pd-hero-equipview';
    this.body.appendChild(this._equipView);
  }

  // ---- Lifecycle ----

  onOpen() {
    // Open on the hero-select tab when explicitly requested, or whenever the
    // player hasn't picked a party yet (first launch). Otherwise straight to equip.
    if (this._openOnSelect || !this.state.heroChosen) {
      this._openOnSelect = false;
      this.switchTab('select');
    } else {
      this.switchTab('equip');
    }
  }

  /**
   * Open the equip view focused on a specific active hero. Called when the
   * player clicks a hero in the game view to manage that exact character.
   * @param {number} index party index (0 or 1)
   */
  focusHero(index) {
    const party = this.state.party ?? [];
    this._equipHeroIndex = Math.max(0, Math.min(index, party.length - 1));
    this.switchTab('equip');
  }

  // ---- Render ----

  render() {
    if (this._activeTab === 'select') {
      this._renderSelect();
    } else {
      this._renderEquip();
    }
  }

  // ---- Tab: Equip ----

  _renderEquip() {
    this._selectView.hidden = true;
    this._equipView.hidden = false;

    const party = this.state.party;
    if (this._equipHeroIndex >= party.length) this._equipHeroIndex = 0;
    const hero = party[this._equipHeroIndex] ?? party[0];
    const cls = this.data.classes[hero.classId];
    if (!cls) return;

    this.root.querySelector('.pd-panel-title').textContent = `HERÓI · ${cls.label}`;

    // Hero selector — ALWAYS visible so the player knows which hero this view
    // is acting on. One chip per active hero (name, level, live HP); a "+" chip
    // offers the second party slot when only one hero is active.
    const heroChips = party.map((h, i) => {
      const hcls = this.data.classes[h.classId];
      const lockMs = lockRemainingMs(h);
      const sub = lockMs > 0
        ? `🔒 ${formatLockTime(lockMs)}`
        : `❤ ${h.hp}/${h.maxHp}`;
      return `<button class="pd-equip-herotab${i === this._equipHeroIndex ? ' active' : ''}" data-hero="${i}">
          <img src="assets/sprites/anim/${h.classId}_walk_0.png" alt="" class="pd-herotab-face">
          <span class="pd-herotab-info">
            <span class="pd-herotab-name">${hcls?.label ?? h.classId} · Nv.${h.level}</span>
            <span class="pd-herotab-sub">${sub}</span>
          </span>
        </button>`;
    }).join('');
    const addChip = party.length < MAX_PARTY
      ? '<button class="pd-equip-herotab pd-herotab-add" data-add="1">＋ 2º Herói</button>'
      : '';
    const heroTabs = `<div class="pd-equip-herotabs">${heroChips}${addChip}</div>`;

    this._equipView.innerHTML = `
      ${heroTabs}
      <div class="pd-section-title">Equipamento</div>
      <div class="pd-panel-stage">
        <div class="pd-panel-col pd-left-col"></div>
        <div class="pd-portrait-block">
          <img class="pd-portrait-img" src="assets/sprites/portraits/${hero.classId}.png" alt="${cls.label}" />
          <span class="pd-portrait-lv">Nv.${hero.level}</span>
          <button class="pd-btn-ghost pd-swap-btn">Trocar Equipe</button>
        </div>
        <div class="pd-panel-col pd-right-col"></div>
      </div>
      <div class="pd-stats-area"></div>
      <div class="pd-consume-area"></div>
      <div class="pd-bag-area"></div>`;

    // Wire hero chips (switch target hero / add a second one)
    this._equipView.querySelectorAll('.pd-equip-herotab').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.dataset.add) { this.switchTab('select'); return; }
        this._equipHeroIndex = Number(btn.dataset.hero) || 0;
        this._renderEquip();
      });
    });

    // Wire swap button (goes to the HERÓIS tab — same as clicking the tab)
    this._equipView.querySelector('.pd-swap-btn').addEventListener('click', () => this.switchTab('select'));

    // Render slots (per-hero)
    renderEquipmentSlots(
      this._equipView.querySelector('.pd-left-col'),
      LEFT_SLOTS, hero, this.data,
      (slot) => { unequip(this.state, hero, slot); this.commit(); }
    );
    renderEquipmentSlots(
      this._equipView.querySelector('.pd-right-col'),
      RIGHT_SLOTS, hero, this.data,
      (slot) => { unequip(this.state, hero, slot); this.commit(); }
    );

    // Stats
    this._renderStats(this._equipView.querySelector('.pd-stats-area'), hero);

    // Consumables
    this._renderConsumables(this._equipView.querySelector('.pd-consume-area'));

    // Bag
    this._renderBag(this._equipView.querySelector('.pd-bag-area'), hero);

    // Attach tooltip hover to bag cells
    this._attachTooltips();
  }

  _renderStats(container, hero) {
    const hs = heroDerivedStats(hero, this.data.classes, hero.equipped);
    // Hero attributes only — gold/kills are ACCOUNT-wide and live in the HUD
    // and STATUS panel; mixing them in here read as stats of this hero.
    container.innerHTML = `
      <div class="pd-section-title">Atributos</div>
      <div class="pd-stat-grid">
        <span>❤ Vida</span><b>${hero.hp}/${hs.maxHp}</b>
        <span>⚔ Ataque</span><b>${Math.round(hs.atk)}</b>
        <span>🛡 Defesa</span><b>${Math.round(hs.def)}</b>
        <span>✦ Magia</span><b>${Math.round(hs.mag)}</b>
        <span>⚡ Vel.</span><b>${hs.atkSpd.toFixed(2)}</b>
        <span>✸ Crít</span><b>${Math.round(hs.crit * 100)}%</b>
        <span>➹ Esquiva</span><b>${Math.round(hs.dodge * 100)}%</b>
      </div>`;
  }

  _renderConsumables(container) {
    const foods = Object.entries(this.state.consumables.foods).filter(([, q]) => q > 0);
    const kits = this.state.consumables.reviveKits ?? 0;
    const foodHtml = foods.length
      ? foods.map(([id, q]) => `<span class="pd-pill">${this.data.loot.foods[id]?.label ?? id} ×${q}</span>`).join('')
      : '<span class="pd-empty">nenhuma</span>';

    container.innerHTML = `
      <div class="pd-section-title">Consumíveis</div>
      <div class="pd-pills">${foodHtml}<span class="pd-pill pd-pill-revive"><img src="assets/sprites/items/icon_revive_kit.png" alt="" class="pd-pill-icon"> Kit ×${kits}</span></div>`;
  }

  _renderBag(container, hero) {
    container.innerHTML = `<div class="pd-section-title">Mochila (${this.state.inventory.length})</div>`;
    const bagEl = document.createElement('div');
    renderBagGrid(bagEl, this.state, this.data, (itemId) => {
      if (equip(this.state, hero, itemId, this.data.classes)) this.commit();
    }, hero);
    container.appendChild(bagEl);
  }

  // ---- Tab: Select (class chooser) ----

  /**
   * Entering the select tab seeds the selection from the active party — but
   * only on entry. The panel re-renders on every tick while open, so the render
   * path only refreshes the highlight from the in-progress selection and never
   * mutates it (the "it reverts to the equipped hero" bug). Seed on switchTab.
   */
  switchTab(tabId) {
    if (tabId === 'select' && this._activeTab !== 'select') {
      this._selectedClasses = this.state.party.map((h) => h.classId);
      this._focusClass = this._selectedClasses[0] ?? CLASS_ORDER[0];
    }
    super.switchTab(tabId);
  }

  _renderSelect() {
    this._equipView.hidden = true;
    this._selectView.hidden = false;

    // First-launch selection (no party chosen yet): hide "Voltar" (there's no
    // equip view to go back to). The close ✕ is ALWAYS available now — dismissing
    // it just starts with the default party (handled in main.js).
    const initial = !this.state.heroChosen;
    this._selectView.querySelector('.pd-sel-back').hidden = initial;
    this._closeBtn.style.visibility = 'visible';

    // Reflect the in-progress selection without mutating it.
    this._refreshSelectUI();
  }

  /** The hero object for a class, whether active or stashed (or null). */
  _classHero(id) {
    return this.state.party.find((h) => h.classId === id) ?? this.state.heroStash?.[id] ?? null;
  }

  /** Level of a class: active hero's level, else its stashed level, else 1. */
  _classLevel(id) {
    return this._classHero(id)?.level ?? 1;
  }

  /** Remaining death-lockout (ms) for a class, or 0 if free to play. */
  _classLockMs(id) {
    return lockRemainingMs(this._classHero(id));
  }

  /** Toggle a class in/out of the selection (cap MAX_PARTY; drops oldest). */
  _pickClass(id) {
    const i = this._selectedClasses.indexOf(id);
    if (i >= 0) {
      this._selectedClasses.splice(i, 1);
    } else {
      if (this._selectedClasses.length >= MAX_PARTY) this._selectedClasses.shift();
      this._selectedClasses.push(id);
    }
    this._focusClass = id;
    this._refreshSelectUI();
  }

  _refreshSelectUI() {
    const focus = this._focusClass ?? this._selectedClasses[0] ?? CLASS_ORDER[0];
    const cls = this.data.classes[focus];
    if (cls) {
      this._selectView.querySelector('.pd-select-name').textContent = cls.label;
      this._selectView.querySelector('.pd-select-desc').innerHTML =
        `<span class="pd-select-role">${ROLE_LABEL[cls.role] ?? cls.role} · ${cls.primary}</span>${cls.mechanic}`;
    }

    const names = this._selectedClasses.map((c) => this.data.classes[c]?.label ?? c);
    const countEl = this._selectView.querySelector('.pd-select-count');
    countEl.textContent = this._selectedClasses.length
      ? `Equipe (${this._selectedClasses.length}/${MAX_PARTY}): ${names.join(' + ')}`
      : `Escolha 1 ou 2 heróis (0/${MAX_PARTY})`;

    this._selectView.querySelectorAll('.pd-hero-card').forEach((c) => {
      c.classList.toggle('selected', this._selectedClasses.includes(c.dataset.id));
      const lockMs = this._classLockMs(c.dataset.id);
      c.classList.toggle('locked', lockMs > 0);
      const lvEl = c.querySelector('.pd-hero-card-lv');
      if (lvEl) {
        lvEl.textContent = lockMs > 0
          ? `🔒 ${formatLockTime(lockMs)}`
          : `Nv.${this._classLevel(c.dataset.id)}`;
      }
    });

    const confirm = this._selectView.querySelector('.pd-sel-confirm');
    confirm.disabled = this._selectedClasses.length === 0;
  }

  // ---- Tooltip ----

  _attachTooltips() {
    const cells = this._equipView.querySelectorAll('.pd-bag-cell, .pd-slot.filled');
    cells.forEach((cell) => {
      cell.addEventListener('pointerenter', (e) => {
        const item = cell._pdItem;
        if (!item) return;
        let compare = null;
        if (cell.dataset.compare) { try { compare = JSON.parse(cell.dataset.compare); } catch { /* ignore */ } }
        const rarityDef = this.data.rarity?.[item.rarity];
        tooltip.show(e.clientX, e.clientY, {
          icon: item.icon,
          name: item.label,
          rarity: item.rarity,
          rarityLabel: rarityDef?.label ?? item.rarity,
          rarityColor: rarityDef?.color ?? '#9d9d9d',
          affixes: item.affixes.map((a) => a.label),
          level: item.itemLevel,
          locked: cell._pdLocked ?? null,
          action: cell._pdAction ?? null,
          compare,
        }, cell.ownerDocument);
      });
      cell.addEventListener('pointerleave', () => tooltip.hide());
      cell.addEventListener('pointermove', (e) => tooltip.moveTo(e.clientX, e.clientY));
    });
  }

  // ---- Forced-open support (called externally via PanelManager) ----

  /**
   * @internal — set by main.js before opening the panel on first launch.
   * Opens on the hero-select tab but stays CLOSABLE (the ✕ works); if the player
   * dismisses it without confirming, main.js starts with the default party.
   */
  prepareForcedSelect() {
    this._openOnSelect = true;
  }
}
